import { useAtomValue } from "jotai";
import { type FunctionComponent, useCallback, useEffect, useState } from "react";

import { config } from "shared/data";
import { accountAtom } from "shared/lib/account";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { rosettaAtom } from "scmp-app/lib/rosetta";

import {
  Container,
  Description,
  Headline,
  StyledIconDailyPlus,
  StyledIconFactSheet,
  StyledIconPerspectives,
  TryForFreeButton,
} from "./styles";

export const PlusPaywall: FunctionComponent = () => {
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);
  const { isLoggedIn } = useAtomValue(accountAtom);
  const subscribeHost = config.rosetta.subscribeHost;
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isSubscriptionEnabled =
    asyncRosettaState?.result?.instance?.rulesEngine?.getOutcome("guest")?.supportPiano;
  const hasScmpPlusUpgradeOptions = asyncRosettaState?.result?.user?.hasScmpPlusUpgradeOptions;
  const isEligibleForSCMPPlusFreeTrialSubscribeOffer =
    asyncRosettaState?.result?.user?.isEligibleForSCMPPlusFreeTrialSubscribeOffer;

  useEffect(() => {
    if (hasTrackedImpression || !isSubscriptionEnabled) return;

    sendGA4Tracking({
      action: "imp",
      category: "subs",
      customized_parameters: {
        call_to_action: hasScmpPlusUpgradeOptions
          ? "upgrade"
          : isEligibleForSCMPPlusFreeTrialSubscribeOffer
            ? "plus trial"
            : "subscribe",
        paywall_status: "hard",
        product: "plus",
        trigger_point: "plus wall",
      },
      subcategory: "entry_point",
    });

    setHasTrackedImpression(true);
  }, [
    hasTrackedImpression,
    hasScmpPlusUpgradeOptions,
    isEligibleForSCMPPlusFreeTrialSubscribeOffer,
    isSubscriptionEnabled,
  ]);

  const handleRenderSubscriptionInformation = useCallback(() => {
    const { ctaLabel, message, redirectUrl } = isSubscriptionEnabled
      ? {
          ctaLabel: isLoggedIn
            ? hasScmpPlusUpgradeOptions
              ? "Upgrade"
              : isEligibleForSCMPPlusFreeTrialSubscribeOffer
                ? "Try for free"
                : "Subscribe"
            : "Try for free or Upgrade",
          message: "To access our exclusive content you’ll need to subscribe.",
          redirectUrl: hasScmpPlusUpgradeOptions
            ? `${config.account.scmpAccountHost}/manage/subscription?upgrade=true`
            : `${subscribeHost}/plus`,
        }
      : {
          ctaLabel: "Go back to SCMP",
          message: "Unfortunately, SCMP Plus is not yet available in your region.",
          redirectUrl: "/",
        };

    const handleClickAction = () => {
      if (isSubscriptionEnabled) {
        sendGA4Tracking({
          action: "click",
          category: "subs",
          customized_parameters: {
            call_to_action: hasScmpPlusUpgradeOptions
              ? "upgrade"
              : isEligibleForSCMPPlusFreeTrialSubscribeOffer
                ? "plus trial"
                : "subscribe",
            paywall_status: "hard",
            product: "plus",
            trigger_point: "plus wall",
          },
          subcategory: "entry_point",
        });
      }

      window.location.href = redirectUrl;
    };

    return (
      <>
        <Description>
          SCMP Plus is a new premium news platform that gives you an all-inclusive edge to stay
          ahead on China news. <b>{message}</b>
        </Description>
        <TryForFreeButton
          onClick={() => {
            handleClickAction();
          }}
        >
          {ctaLabel}
        </TryForFreeButton>
      </>
    );
  }, [
    isSubscriptionEnabled,
    isLoggedIn,
    hasScmpPlusUpgradeOptions,
    isEligibleForSCMPPlusFreeTrialSubscribeOffer,
    subscribeHost,
  ]);

  return (
    <Container>
      <Headline>Not an SCMP Plus subscriber?</Headline>
      {handleRenderSubscriptionInformation()}
      {isSubscriptionEnabled && (
        <ul>
          <li>
            <StyledIconDailyPlus />
            <p>
              Save time and identify what matters with our newsletter (Daily Pulse) and calendar of
              key events (News Agenda).
            </p>
          </li>
          <li>
            <StyledIconFactSheet />
            <p>
              Connect the dots and navigate complexities with our best-in-class visualisations
              (FactSheet) plus interactive infographics.
            </p>
          </li>
          <li>
            <StyledIconPerspectives />
            <p>
              Explore diverse perspectives from China experts and columnists to develop informed
              views.
            </p>
          </li>
        </ul>
      )}
    </Container>
  );
};

PlusPaywall.displayName = "PlusPaywall";
